import Phaser from 'phaser';
import { BIOMES, type BiomeId } from '../../content/biomes';
import {
  BUILDER_FLOOR_PRICE,
  BUILDER_SHOP,
  builderOpen,
  builderPrice,
  buyBuilder,
  buyKit,
  deleteLawn,
  kitMet,
  kitPrice,
  lawnSize,
  MAX_CUSTOM_LAWNS,
  type CustomLawn,
} from '../../meta/customLawns';
import { drawCoin } from '../ui/widgets';
import { saves } from '../records';
import { bevelButton, clearScene, navyPanel, pager } from '../ui/chrome';
import { arriveSince, celebratePurchase } from '../ui/motion';
import { drawHeader, drawTabBar, HEADER_H, TABBAR_H } from '../ui/navChrome';
import { label, UI } from '../ui/text';
import { drawWorldIcon } from '../ui/zoneArt';
import { playCustomLawn } from './customPlay';
import { pasteLawn } from './sharedLawn';
import { decodeLawn, encodeLawn } from '../../meta/lawnCode';
import type { CommunityLawn } from '../../meta/online/schema';
import { communityLawns, onlineOn, shareLawnOnline } from '../online';
import { wipeTo } from './WipeScene';

const ROW_H = 34;
const ROW_GAP = 4;

/**
 * My Lawns (lm-ocm.6): the lawns made in the builder, newest first: each with its yard, name,
 * size and whether it has been checked, PLAY (once checked), EDIT, and a delete that asks first.
 * NEW LAWN starts another.
 */
export class MyLawnsScene extends Phaser.Scene {
  private page = 0;
  /** The lawn whose delete is asking "sure?". */
  private confirming: string | null = null;
  private message = '';
  /** Showing the kits instead of the lawns. */
  private kits = false;
  /** A purchase just went through: the next draw flies the coins to it (as in the Garage). */
  private purchase: { from: number; what: 'builder' | BiomeId } | null = null;
  /** Where the next draw shows what was just bought (the coins' landing point). */
  private bought: { x: number; y: number } | null = null;
  /** The player's lawns that are up in the community, by code (online scores on). */
  private online = new Map<string, CommunityLawn>();

  constructor() {
    super('MyLawns');
  }

  create(data: { message?: string } = {}): void {
    this.page = 0;
    this.confirming = null;
    this.message = data.message ?? '';
    this.kits = false;
    this.purchase = null;
    this.cameras.main.setBackgroundColor('#15102f');
    // Redrawn when the screen's height changes (rotation, the safe area settling).
    const redraw = () => this.draw();
    this.scale.on(Phaser.Scale.Events.RESIZE, redraw);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () =>
      this.scale.off(Phaser.Scale.Events.RESIZE, redraw),
    );
    this.draw(true);
    // Which of these lawns are up in the community, and how they are doing.
    this.online = new Map();
    if (onlineOn()) {
      void communityLawns('mine').then((list) => {
        if (!list || !this.scene.isActive()) return;
        this.online = new Map(list.lawns.map((l) => [l.code, l]));
        this.draw();
      });
    }
  }

  private draw(entering = false): void {
    // A purchase's coins and stamp belong to the page they were bought on.
    this.tweens.killAll();
    this.time.removeAllEvents();
    clearScene(this);
    const { width, height } = this.scale.gameSize;
    const header = drawHeader(this, 'MY LAWNS', 'runs');
    this.bought = null;
    this.drawPage(width, height, entering);
    drawTabBar(this, 'runs');
    const purchase = this.purchase;
    this.purchase = null;
    if (purchase && this.bought) {
      celebratePurchase(this, {
        wallet: header.wallet,
        from: purchase.from,
        to: saves().save.coins,
        target: this.bought,
        stamp: 'YOURS!',
      });
    }
  }

  private drawPage(width: number, height: number, entering: boolean): void {
    const save = saves().save;
    if (!save.builder.owned) {
      this.drawUnlock(width);
      return;
    }
    if (this.kits) {
      this.drawKits(width, height);
      return;
    }
    const lawns = save.customLawns;
    let y = HEADER_H + 8;
    const full = lawns.length >= MAX_CUSTOM_LAWNS;
    // NEW LAWN, PASTE (a friend's lawn code), KITS.
    const kitsW = 40;
    const pasteW = 46;
    const newW = width - 20 - kitsW - pasteW;
    // The builder just bought: its first button is where the coins land.
    if (this.purchase?.what === 'builder') this.bought = { x: 6 + newW / 2, y: y + 11 };
    bevelButton(
      this,
      6,
      y,
      newW,
      22,
      full ? 'FULL' : 'NEW LAWN',
      () => !full && wipeTo(this, 'Builder', { fresh: true }),
      !full,
    );
    bevelButton(this, width - 10 - kitsW - pasteW, y, pasteW, 22, 'PASTE', () => this.paste());
    bevelButton(this, width - 6 - kitsW, y, kitsW, 22, 'KITS', () => {
      this.kits = true;
      this.message = '';
      this.draw();
    });
    y += 28;
    // Lawns other players built and shared.
    if (onlineOn()) {
      bevelButton(this, 6, y, width - 12, 18, 'COMMUNITY LAWNS', () => wipeTo(this, 'Community'));
      y += 24;
    }
    if (this.message) {
      label(this, width / 2, y, this.message, 1, UI.accent).setOrigin(0.5, 0);
      y += 12;
    }
    const bottom = height - TABBAR_H - 6;
    if (lawns.length === 0) {
      label(this, width / 2, y + 10, 'No lawns yet.', 1, UI.dim).setOrigin(0.5, 0);
      label(this, width / 2, y + 22, 'Make one: paint, check, play!', 1, UI.dim).setOrigin(0.5, 0);
    } else {
      const perPage = Math.max(1, Math.floor((bottom - y - 12) / (ROW_H + ROW_GAP)));
      const pages = Math.ceil(lawns.length / perPage);
      this.page = Math.min(this.page, pages - 1);
      pager(this, width - 6, y, this.page, pages, (p) => {
        this.page = p;
        this.confirming = null;
        this.draw();
      });
      label(this, 6, y, `${lawns.length} OF ${MAX_CUSTOM_LAWNS}`, 1, UI.dim);
      y += 12;
      lawns.slice(this.page * perPage, (this.page + 1) * perPage).forEach((lawn, i) => {
        const mark = this.children.length;
        this.drawRow(lawn, y + i * (ROW_H + ROW_GAP), width);
        if (entering) arriveSince(this, mark, 0, 8, 40 + i * 50, 240);
      });
    }
  }

  /**
   * One lawn, on two lines: its yard and name; then its size and state (READY once checked, else
   * DRAFT) with PLAY, EDIT and a delete cross (which asks first).
   */
  private drawRow(lawn: CustomLawn, y: number, width: number): void {
    const g = this.add.graphics();
    navyPanel(g, 4, y, width - 8, ROW_H);
    drawWorldIcon(g, lawn.biome, 10, y + 5);
    label(this, 24, y + 6, lawn.name.toUpperCase(), 1, UI.accent);
    const { width: w, height: h } = lawnSize(lawn);
    label(this, 10, y + 21, `${w}x${h}`, 1, UI.dim);
    label(this, 46, y + 21, lawn.par ? 'READY' : 'DRAFT', 1, lawn.par ? UI.good : UI.dim);
    // Online: POST a ready lawn to the community, or how it is doing there.
    if (lawn.par && onlineOn()) {
      const up = this.online.get(encodeLawn(lawn));
      if (up) {
        label(
          this,
          width - 10,
          y + 6,
          `${up.plays} PLAY${up.plays === 1 ? '' : 'S'}`,
          1,
          UI.good,
        ).setOrigin(1, 0);
      } else {
        bevelButton(this, width - 44, y + 3, 36, 12, 'POST', () => void this.post(lawn));
      }
    }
    const by = y + 16;
    if (this.confirming === lawn.id) {
      // Asking first: DELETE (for good) or KEEP.
      bevelButton(this, width - 94, by, 46, 14, 'DELETE', () => this.remove(lawn), true);
      bevelButton(this, width - 46, by, 38, 14, 'KEEP', () => {
        this.confirming = null;
        this.draw();
      });
      return;
    }
    const play = () => {
      if (!playCustomLawn(this, lawn)) {
        this.message = 'None of your mowers clear it.';
        this.draw();
      }
    };
    if (lawn.par) bevelButton(this, width - 98, by, 34, 14, 'PLAY', play, true);
    bevelButton(this, width - 60, by, 32, 14, 'EDIT', () =>
      wipeTo(this, 'Builder', { lawnId: lawn.id }),
    );
    const x = label(this, width - 16, by + 4, 'X', 1, UI.dim).setOrigin(0.5, 0);
    this.add
      .zone(x.x - 8, by - 2, 16, 18)
      .setOrigin(0)
      .setData('label', 'DELETE')
      .setInteractive({ useHandCursor: true })
      .on('pointerup', () => {
        this.confirming = lawn.id;
        this.draw();
      });
  }

  /**
   * Before the builder is bought: what it is, its price (and how it drops: 10% a zone cleared,
   * shown up front so buying early is an informed trade), and BUY, or the lawn that opens it.
   */
  private drawUnlock(width: number): void {
    const save = saves().save;
    const open = builderOpen(save);
    const price = builderPrice(save) ?? 0;
    const g = this.add.graphics();
    const top = HEADER_H + 10;
    navyPanel(g, 4, top, width - 8, 112);
    label(this, width / 2, top + 8, 'LAWN BUILDER', 2, UI.accent).setOrigin(0.5, 0);
    const lines = [
      'Make your own lawns:',
      'paint, check, play them.',
      'Comes with the Backyard kit;',
      'more kits in each zone.',
    ];
    lines.forEach((t, i) =>
      label(this, width / 2, top + 30 + i * 10, t, 1, i < 2 ? UI.text : UI.dim).setOrigin(0.5, 0),
    );
    label(
      this,
      width / 2,
      top + 76,
      `Drops 10% a zone, to ${BUILDER_FLOOR_PRICE}.`,
      1,
      UI.dim,
    ).setOrigin(0.5, 0);
    if (this.message) {
      label(this, width / 2, top + 92, this.message, 1, UI.accent).setOrigin(0.5, 0);
    }
    const by = top + 122;
    // Community lawns play without the builder too.
    if (onlineOn()) {
      bevelButton(this, 6, by + 58, width - 12, 20, 'COMMUNITY LAWNS', () =>
        wipeTo(this, 'Community'),
      );
    }
    // A friend's lawn plays without the builder.
    bevelButton(this, 6, by + 34, width - 12, 20, "PASTE A FRIEND'S LAWN", () => this.paste());
    if (!open) {
      label(this, width / 2, by + 8, `AFTER LAWN ${BUILDER_SHOP.opensAfter}`, 1, UI.dim).setOrigin(
        0.5,
        0,
      );
      return;
    }
    const afford = save.coins >= price;
    bevelButton(
      this,
      6,
      by,
      width - 12,
      26,
      `BUY  ${price}`,
      () => this.buyBuilder(),
      afford,
      2,
      4,
    );
  }

  /** POST: shares a ready lawn with the community. */
  private async post(lawn: CustomLawn): Promise<void> {
    const code = encodeLawn(lawn);
    // A code carries only word-list names: this copy takes the name the community will show.
    const decoded = decodeLawn(code);
    if (decoded.ok && decoded.lawn.name !== lawn.name) {
      const name = decoded.lawn.name;
      saves().update((s) => {
        s.customLawns = s.customLawns.map((l) => (l.id === lawn.id ? { ...l, name } : l));
      });
    }
    const result = await shareLawnOnline(code);
    if (!this.scene.isActive()) return;
    if (typeof result === 'string') {
      this.message = result;
    } else {
      this.message = 'Posted! Others can play it now.';
      const mine = await communityLawns('mine');
      if (mine) this.online = new Map(mine.lawns.map((l) => [l.code, l]));
    }
    this.draw();
  }

  /** PASTE: a lawn code from the clipboard (a friend's), checked and played. */
  private paste(): void {
    void pasteLawn(this).then((problem) => {
      if (!problem || !this.scene.isActive()) return;
      this.message = problem;
      this.draw();
    });
  }

  private buyBuilder(): void {
    let ok = false;
    const from = saves().save.coins;
    saves().update((s) => (ok = buyBuilder(s)));
    if (ok) this.purchase = { from, what: 'builder' };
    this.message = ok ? 'The builder is yours!' : 'Not enough coins yet.';
    this.draw();
  }

  /** The kits: each zone's pieces, owned or with its price (cheaper once all are met). */
  private drawKits(width: number, height: number): void {
    const save = saves().save;
    let y = HEADER_H + 8;
    bevelButton(this, 6, y, 60, 18, '< LAWNS', () => {
      this.kits = false;
      this.message = '';
      this.draw();
    });
    if (this.message) label(this, width - 8, y + 6, this.message, 1, UI.accent).setOrigin(1, 0);
    y += 24;
    const bottom = height - TABBAR_H - 4;
    const rowH = Math.max(22, Math.min(28, Math.floor((bottom - y) / BIOMES.length) - 2));
    BIOMES.forEach((b, i) => {
      const ry = y + i * (rowH + 2);
      const g = this.add.graphics();
      g.fillStyle(UI.panel).fillRect(4, ry, width - 8, rowH);
      g.lineStyle(1, UI.rule).strokeRect(4.5, ry + 0.5, width - 9, rowH - 1);
      drawWorldIcon(g, b.id, 9, ry + Math.round(rowH / 2) - 5);
      if (this.purchase?.what === b.id) this.bought = { x: 14, y: ry + Math.round(rowH / 2) };
      // Two lines: the zone; then its price (and "met", 30% off once its pieces are all met).
      const top = ry + Math.round(rowH / 2) - 9;
      label(this, 23, top, b.name.toUpperCase(), 1, UI.accent);
      const price = kitPrice(save, b.id);
      if (price === null) {
        label(this, 23, top + 10, 'Owned', 1, UI.good);
        return;
      }
      drawCoin(this.add.graphics(), 23, top + 10);
      const p = label(this, 33, top + 10, String(price), 1, save.coins >= price ? UI.text : UI.dim);
      if (kitMet(save, b.id)) label(this, p.x + p.width + 6, top + 10, 'met: -30%', 1, UI.dim);
      bevelButton(this, width - 42, ry + Math.round(rowH / 2) - 7, 34, 14, 'BUY', () =>
        this.buyKit(b.id),
      );
    });
  }

  private buyKit(zone: BiomeId): void {
    let ok = false;
    const from = saves().save.coins;
    saves().update((s) => (ok = buyKit(s, zone)));
    if (ok) this.purchase = { from, what: zone };
    // Short: it sits beside < LAWNS (a zone's name ran into it). The stamp marks which kit.
    this.message = ok ? 'Kit bought!' : 'Not enough coins.';
    this.draw();
  }

  private remove(lawn: CustomLawn): void {
    saves().update((s) => deleteLawn(s, lawn.id));
    this.confirming = null;
    this.message = `${lawn.name} deleted.`;
    this.draw();
  }
}
